<template>
  <div id="app">

    <Nav></Nav>

    <div v-if="hasLoaded" uk-height-viewport="expand: true">
      <EndpointSelector id="mp-ep-selector" :schema="schema" :index="selectedEndpointIndex" @endpoint-changed="onSelectedIndexChange"></EndpointSelector>

      <div class="uk-padding uk-padding-remove-horizontal">
        <div class="uk-container">
          <form class="uk-form-stacked" v-on:submit.prevent="onSubmitRequest">

            <div class="uk-grid uk-child-width-1-1" uk-grid>
              <div><CardApiKey :api-key.sync="request.apiKey"></CardApiKey></div>

              <div id="mp-container-endpoint" :class="`${classes.container} uk-margin-medium-top`">
                <div class="uk-grid uk-grid-small uk-child-width-1-1" uk-grid>
                  <EndpointOverview :endpoint="selectedEndpoint"></EndpointOverview>
                  <EndpointParameters :schema="schema" :endpoint="selectedEndpoint" v-bind.sync="request.params" :request-params="request.params" :is-request-in-flight="isRequestInFlight"></EndpointParameters>

                  <!-- Submit / Reset Buttons -->
                  <div class="uk-width-1-1 uk-text-center uk-flex-middle" v-if="!selectedEndpoint.disabled">
                    <button type="submit" class="uk-button uk-button-primary uk-margin-right" :disabled="isRequestInFlight">Submit</button>
                    <button type="button" class="uk-button uk-button-default" :disabled="isRequestInFlight" @click="mapDynamicParametersToRequest()">Reset</button>
                  </div>

                </div>
              </div>
            </div>
          </form>

          <transition name="fade">
            <EndpointResponse v-show="response.data" :response="response" :requestInfo="requestInfo"></EndpointResponse>
          </transition>

          <!-- Back to Top Button -->
          <div class="uk-text-right uk-margin-top">
            <a href="#mp-ep-selector" class="uk-text-bold mp-text-decoration-none">Back to top <i class="far fa-chevron-up"></i></a>
          </div>

        </div>
      </div>
    </div>

    <div v-else  uk-height-viewport="expand: true" class="uk-flex uk-flex-middle uk-flex-center">
      <div class="uk-padding uk-padding-remove-horizontal">
        <div class="uk-container uk-text-center uk-hidden">
          <p>Loading...</p>
        </div>
      </div>
    </div>

    <Footer></Footer>

  </div>
</template>

<script>
import Nav from './components/Nav.vue'
import CardApiKey from "./components/CardApiKey";
import EndpointSelector from './components/EndpointSelector.vue'
import EndpointOverview from './components/EndpointOverview.vue'
import EndpointParameters from './components/EndpointParameters.vue'
import EndpointResponse from './components/EndpointResponse.vue'
import Footer from "./components/Footer";
import DateTime from "luxon/src/datetime";

export default {
  name: 'App',
  components: {
    Footer, Nav, CardApiKey, EndpointSelector, EndpointOverview, EndpointParameters, EndpointResponse
  },

  data: function () {
    return {
      selectedEndpointIndex: 0,
      request: { apiKey: window.webpackHotUpdate ? "84176380-6a98-11ea-a782-a36d1f11b473": "", params: {} },
      response: { data: false, code: false, message: false },
      classes: { container: "" },
      isRequestInFlight: false,
      requestInfo: { url: "", params: "", method: "" },
      hasLoaded: false
    }
  },

  computed: {
    /**
     * Shorthand accessor to the currently selected endpoint
     *
     * @returns {Object} The currently selected endpoint schema object
     */
    selectedEndpoint: function () {
      return this.schema.endpoints[this.selectedEndpointIndex]
    },

    schema: function() {
      return this.$rp.schema
    }
  },

  created() {
    this.$bus.$on("loaded", this.onDataLoaded)
  },

  methods: {
    onDataLoaded: function() {
      // Automatically show an endpoint if specified in tool query params
      if(location.search !== "") {
        let linkedEndpointName = decodeURI(location.search.substr(1).split("=")[1])
        let linkedEndpointIndex = this.schema.endpoints.findIndex((endpoint) => {
          return endpoint.name === linkedEndpointName
        });
        this.selectedEndpointIndex = linkedEndpointIndex > -1 ? linkedEndpointIndex : 0
      }

      this.hasLoaded = true

      // For .sync to work with our dynamic parameters - alert the Vue instance to make the new object properties reactive
      this.mapDynamicParametersToRequest()
    },

    /**
     * Handle change in the selected endpoint
     *
     * @param {integer} newIndex The index of the selected endpoint within the endpoint array
     */
    onSelectedIndexChange: function(newIndex) {
      this.response = { data: false, code: false, message: false }
      this.requestInfo = { url: "", params: "", method: "" }
      this.classes.container = "fade-out"
      setTimeout(() => {
        this.selectedEndpointIndex = newIndex
        this.mapDynamicParametersToRequest()
        this.classes.container = "fade-in"
      }, 500)
    },

    /**
     * Handle submission of the endpoint request
     */
    onSubmitRequest: function() {
      // Refuse if a request is in flight already
      if(this.isRequestInFlight)
        return

      this.response = { data: false, code: false, message: false }
      this.isRequestInFlight = true
      let parameters = this.processParams()

      // Replace any path parameters with their values
      // Note: We preserve commas in path parameters (e.g., for comma-separated station IDs)
      // because the API expects literal commas, not URL-encoded %2C
      let url = this.selectedEndpoint.endpoint.replace(/\{([a-zA-Z]+)\}/g, function (_, name) {
        let value = encodeURIComponent(parameters[name]).replace(/%2C/g, ',');
        delete parameters[name];
        return value
      });

      // Build the query string for GET requests
      let parametersAsQueryString = ""
      if(this.selectedEndpoint.method === "GET"){
        parametersAsQueryString = Object.keys(parameters).map(function(key) {
          let encodedParameter = key + '='

          if (key === "bearerId") {
            encodedParameter += parameters[key];
          } else {
            encodedParameter += encodeURIComponent(parameters[key]);
          }

          const keysToNotEncode = ["geo", "bearerId", "category"]
          if(!keysToNotEncode.includes(key)){ // Edge in WRAPI - Geo & bearer . shouldnt be encoded, but ODID . should be
            encodedParameter = encodedParameter.replace(/\./g, "%2B"); // encodeURIComponent doesn't handle full stops
          }

          return encodedParameter
        }).join("&");
        parameters = {}
      }

      let displayableQueryString = parametersAsQueryString === "" ? "" : "?" + parametersAsQueryString
      let actualQueryString = parametersAsQueryString === "" ? "" : "?" + parametersAsQueryString

      // Endpoints can be relative to the schema server, or absolute. Handle both
      let finalUrl = this.selectedEndpoint.absolute ? url + actualQueryString : this.schema.server + url + actualQueryString

      // Build up request info for display
      this.requestInfo.url = url + displayableQueryString
      this.requestInfo.method = this.selectedEndpoint.method
      this.requestInfo.params = parameters
      
      // For POST requests, show additional request info
      if (this.selectedEndpoint.method === "POST") {
        this.requestInfo.contentType = 'application/json';
        // For Recommendations endpoint, show the formatted request body
        if (this.selectedEndpoint.name === "Recommendations") {
          this.requestInfo.requestBody = JSON.stringify(parameters, null, 2);
        }
      }

      // Create authentication header with proper signature format
      const authHeader = `Signature keyId="${this.request.apiKey}",algorithm="rsa-sha256",signature="test"`
      
      // Setup headers with authentication
      const headers = {
        'Authorization': authHeader
      };
      
      // Add Content-Type header for POST requests
      if (this.selectedEndpoint.method === "POST") {
        headers['Content-Type'] = 'application/json';
      }
      
      // Make the request using Axios (this.$http)
      this.$http({
        method: this.selectedEndpoint.method,
        url: finalUrl,
        headers: headers,
        data: parameters
      }).then(response => {
        console.log('API Request Successful:', finalUrl);
        this.response =  { data: response.data, code: response.status, message: "Success" }
      })
      .catch(error => {
        console.error('API Request Failed:', finalUrl, error);
        
        if (error.response) {
          // Non 2XX Error
          const errorMessage = error.response.data && error.response.data.error 
            ? error.response.data.error 
            : `Server responded with status code ${error.response.status}`;
          
          this.response = { 
            data: error.response, 
            code: error.response.status, 
            message: errorMessage,
            requestDetails: {
              url: finalUrl,
              method: this.selectedEndpoint.method,
              headers: { 
                Authorization: authHeader.replace(this.request.apiKey, '******'),
                ...(this.selectedEndpoint.method === "POST" ? { 'Content-Type': 'application/json' } : {})
              },
              body: this.selectedEndpoint.name === "Recommendations" ? JSON.stringify(parameters, null, 2) : null
            }
          }
        } else if (error.request) {
          // The request was made but no response was received
          this.response = { 
            data: error.toJSON(), 
            code: false, 
            message: `No response received: ${error.message}`,
            requestDetails: {
              url: finalUrl,
              method: this.selectedEndpoint.method
            }
          }
        } else {
          // Something happened in setting up the request that triggered an Error
          this.response = { 
            data: error.toJSON(), 
            code: false, 
            message: `Request error: ${error.message}`
          }
        }
      })
      .then(() => {
        this.isRequestInFlight = false
      })
    },

    /**
     * Process the request parameters input into the right format, ready for Axios
     *
     * @return {Object} The processed request parameters
     */
    processParams() {
      // Special handling for Recommendations endpoint 
      if (this.selectedEndpoint.name === "Recommendations") {
        let params = {};
        for (let [key, value] of Object.entries(this.request.params)) {
          // Keep factors as an array
          if (key === "factors" && Array.isArray(value)) {
            params[key] = value;
          }
          // Convert latitude and longitude to numbers
          else if ((key === "latitude" || key === "longitude") && value) {
            params[key] = parseFloat(value);
          }
          // Handle other parameters
          else if (value) {
            params[key] = value;
          }
        }
        return params;
      }
      
      // For other endpoints, use the existing logic
      let params = {};
      for (let [key, value] of Object.entries(this.request.params)) {
        if(Array.isArray(value)) {
          params[key] = value.join(",")
        } else if((!!value) && (value.constructor === Object)) {
          // Only 1 type is currently represented by an object - geo. Encode.
          params[key] = Object.keys(value).map(function (key) {
            return value[key]
          }).join(",");
        } else if(key === "from" || key === "to") {
          // Hack to handle weird date representation
          if(value !== ""){
            params[key] = DateTime.fromISO(value).toFormat("y-LL-dd'T'HH:mm:ss'.'0000");
          }
        } else if(value) {
          params[key] = value
        }
      }
      return params
    },

    /**
     * Build the default parameters object into one that represents the selected endpoint (for value syncing)
     */
    mapDynamicParametersToRequest() {
      this.request.params = {}
      for (let [, parameter] of Object.entries(this.selectedEndpoint.parameters)) {
        let defaultValue = ""
        switch(parameter.type) {
          case "point":
            defaultValue = { latitude: "", longitude: "" }
            break
        }
        if(parameter.default !== undefined){
          defaultValue = parameter.default
        } else if(this.schema.types[parameter.type]?.default !== undefined) {
          defaultValue = this.schema.types[parameter.type].default
        }
        this.$set(this.request.params, parameter.name, defaultValue)
      }
    }
  }
}
</script>

<style lang="scss">
  // Endpoint switch fade
  #mp-container-endpoint {
    transition: opacity 0.5s;

    &.fade-out {
      opacity: 0;
    }

    &.fade-in {
      opacity: 1;
    }
  }

  // Default Vue simple fade transition (for the response area)
  .fade-enter-active {
    transition: opacity .5s;
  }
  .fade-leave-active {
    transition: opacity 0s;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
</style>
